import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown, Switch, Table } from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import BaseButton from 'components/atoms/baseButton';
import BaseLoader from 'components/atoms/base-loader';
import { DateFormatter } from 'infrastructure/functions/format-date';
import { DateFormats } from 'infrastructure/enums';
import { showResult } from 'infrastructure/hooks/utils';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import PapFormUploadModal from 'components/organisms/oneplatform/pap-form-upload-modal';
import { SUCCESS_MESSAGE } from 'constants/messages';

import s from './styles.module.scss';

import type {
  IAdminPapForm,
  IAdminPapFormsResponse,
  IEditPapFormPayload,
  IUploadPapFormPayload,
} from 'infrastructure/interfaces';
import type { MenuProps, TableProps } from 'antd';
import type { useAdminMedicationsActions } from 'infrastructure/hooks/medications';

const DATE_FORMAT = DateFormats['MM-dd-yyyy'];

type MedicationActions = ReturnType<typeof useAdminMedicationsActions>;

interface MedicationPapFormsProps {
  medicationGuid?: string;
  papForms?: IAdminPapFormsResponse | null;
  isLoading: boolean;
  refetchPapForms: () => Promise<any>;
  refetchMedication: () => Promise<any>;
  actions: MedicationActions;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize: number) => void;
}

const MedicationPapForms: React.FC<MedicationPapFormsProps> = ({
  medicationGuid,
  papForms,
  isLoading,
  refetchPapForms,
  refetchMedication,
  actions,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPapForm, setEditingPapForm] = useState<IAdminPapForm | null>(
    null,
  );
  const [activatingGuid, setActivatingGuid] = useState<string | null>(null);
  const [formNameError, setFormNameError] = useState<string | undefined>(
    undefined,
  );
  const forms = papForms?.items ?? [];
  const refreshAll = useCallback(async () => {
    await Promise.all([refetchPapForms(), refetchMedication()]);
  }, [refetchPapForms, refetchMedication]);

  const submitting =
    actions.isCreating ||
    actions.isConfirming ||
    actions.isEditing ||
    actions.isUpdatingStatus;

  const openCreateModal = useCallback(() => {
    if (!medicationGuid) return;
    if (submitting) return;
    setEditingPapForm(null);
    setFormNameError(undefined);
    setModalOpen(true);
  }, [medicationGuid, submitting]);

  const openEditModal = useCallback(
    (papForm: IAdminPapForm) => {
      if (submitting) return;
      setEditingPapForm(papForm);
      setFormNameError(undefined);
      setModalOpen(true);
    },
    [submitting],
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingPapForm(null);
    setFormNameError(undefined);
  }, []);

  const triggerDownload = (url: string, filename?: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename ?? 'pap-form.pdf');
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resolvePapFormUrl = useCallback(
    async (papForm: IAdminPapForm) => {
      if (papForm.downloadUrl) return papForm.downloadUrl;
      const response = await actions.downloadPapForm(papForm.guid);
      if (response?.downloadUrl) return response.downloadUrl;

      showResult(
        t(
          'messages.papFormDownloadError',
          'Downloading error, please try again.',
        ),
      );
      return undefined;
    },
    [actions, t],
  );

  const handleDownload = useCallback(
    async (papForm: IAdminPapForm) => {
      const url = papForm.downloadUrl || (await resolvePapFormUrl(papForm));
      if (url) triggerDownload(url, papForm.formName);
    },
    [resolvePapFormUrl],
  );

  const handleCreate = useCallback(
    async (payload: IUploadPapFormPayload) => {
      if (!medicationGuid || !payload.file) return;

      const fileName = payload.file?.name ?? '';
      const fileSize = payload.file?.size ?? 0;
      const name = payload.formName ?? fileName;

      try {
        const created = await actions.createPapForm({
          medicationGroupKey: medicationGuid,
          name,
          notes: payload.notes,
          fileName,
          fileSize,
        });
        await actions.uploadPapFormToS3(created.uploadUrl, payload.file);
        await actions.confirmPapForm(created.guid);

        const isFirstForm = forms.length === 0;
        if (isFirstForm) {
          await actions.updatePapFormStatus(created.guid, { status: 'active' });
        }

        notificationController.success({
          message: t('messages.papFormUploadSuccess', SUCCESS_MESSAGE),
        });
        await refreshAll();
        return true;
      } catch (error: any) {
        if (
          error?.message?.includes('already exists') ||
          error?.response?.data?.message?.includes('already exists')
        ) {
          setFormNameError(
            t(
              'messages.papFormNameAlreadyExists',
              'A PAP form with this name already exists for this medication',
            ),
          );
          return false;
        }
        throw error;
      }
    },
    [actions, medicationGuid, t, refreshAll, forms.length],
  );

  const handleEdit = useCallback(
    async (papForm: IAdminPapForm, payload: IUploadPapFormPayload) => {
      const { file, formName, notes } = payload;

      const editPayload: IEditPapFormPayload = {
        name: formName?.trim() || undefined,
        notes: notes?.trim() ? notes.trim() : null,
      };

      if (file) {
        editPayload.fileName = file.name;
        editPayload.fileSize = file.size;
      }

      try {
        const res = await actions.editPapForm(papForm.guid, editPayload);

        if (file && res?.uploadUrl) {
          await actions.uploadPapFormToS3(res.uploadUrl, file);
        }

        notificationController.success({
          message: t('messages.papFormEditSuccess', SUCCESS_MESSAGE),
        });
        await refreshAll();
        return true;
      } catch (error: any) {
        if (
          error?.message?.includes('already exists') ||
          error?.response?.data?.message?.includes('already exists')
        ) {
          setFormNameError(
            t(
              'messages.papFormNameAlreadyExists',
              'A PAP form with this name already exists for this medication',
            ),
          );
          return false;
        }
        throw error;
      }
    },
    [actions, t, refreshAll],
  );

  const handleToggleStatus = useCallback(
    async (papForm: IAdminPapForm, shouldBeActive: boolean) => {
      if (activatingGuid || isLoading) return;

      const newStatus = shouldBeActive ? 'active' : 'retired';

      setActivatingGuid(papForm.guid);
      try {
        if (shouldBeActive) {
          const currentlyActive = forms.find(
            (f) => f.status === 'active' && f.guid !== papForm.guid,
          );

          if (currentlyActive) {
            await actions.updatePapFormStatus(currentlyActive.guid, {
              status: 'retired',
            });
          }
        }

        await actions.updatePapFormStatus(papForm.guid, { status: newStatus });

        notificationController.success({
          message: shouldBeActive
            ? t(
                'messages.papFormSetActiveSuccess',
                'PAP form enabled successfully',
              )
            : t(
                'messages.papFormSetInactiveSuccess',
                'PAP form disabled successfully.',
              ),
        });

        await refreshAll();
      } finally {
        setActivatingGuid(null);
      }
    },
    [actions, activatingGuid, isLoading, t, refreshAll, forms],
  );

  const handleSubmit = useCallback(
    async ({ file, formName, notes }: IUploadPapFormPayload) => {
      setFormNameError(undefined);
      if (editingPapForm) {
        const success = await handleEdit(editingPapForm, {
          file,
          formName,
          notes,
        });
        if (success) {
          closeModal();
        }
        return;
      }

      if (!file) {
        showResult(t('messages.papFormFileRequired'));
        return;
      }

      const success = await handleCreate({ file, formName, notes });
      if (success) {
        closeModal();
      }
    },
    [editingPapForm, handleEdit, closeModal, handleCreate, t],
  );

  const columns: TableProps<IAdminPapForm>['columns'] = useMemo(
    () => [
      {
        title: t('labels.active', 'Active'),
        key: 'status',
        width: 120,
        render: (_, record) => {
          const isActive = record.status === 'active';
          const isBusy = activatingGuid === record.guid;

          return (
            <div className={s.activeCell}>
              <Switch
                checked={isActive}
                loading={isBusy}
                disabled={submitting || actions.isDeleting || isBusy}
                onChange={(checked) => handleToggleStatus(record, checked)}
              />
            </div>
          );
        },
      },
      {
        title: t('labels.formName', 'Form Name'),
        dataIndex: 'formName',
        key: 'formName',
      },
      {
        title: t('labels.fileType', 'File Type'),
        dataIndex: 'fileType',
        key: 'fileType',
        width: 120,
        render: (value: string) => value?.toUpperCase() ?? 'PDF',
      },
      {
        title: t('labels.uploadedDate', 'Uploaded Date'),
        dataIndex: 'uploadedAt',
        key: 'uploadedAt',
        width: 180,
        render: (value: string) =>
          value ? DateFormatter({ date: value, format: DATE_FORMAT }) : '—',
      },
      {
        title: t('labels.uploadedBy', 'Uploaded By'),
        dataIndex: 'uploadedBy',
        key: 'uploadedBy',
        width: 200,
        render: (value: { guid: string; name: string; email: string }) =>
          value?.name || '—',
      },
      {
        title: t('labels.notes', 'Notes'),
        dataIndex: 'notes',
        key: 'notes',
        render: (value?: string) => (
          <span className={s.notes}>{value || '—'}</span>
        ),
      },
      {
        title: t('labels.actions', 'Actions'),
        key: 'menu',
        width: 56,
        render: (_, record) => {
          const isDownloading = actions.downloadingPapFormGuid === record.guid;

          const items: MenuProps['items'] = [
            {
              key: 'download',
              label: t('controls.download', 'Download'),
              onClick: () => handleDownload(record),
              disabled: isDownloading,
            },
            {
              key: 'edit',
              label: t('controls.edit', 'Edit'),
              onClick: () => openEditModal(record),
              disabled: submitting,
            },
          ];

          return (
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              placement="bottomRight"
            >
              <button
                type="button"
                className={s.moreBtn}
                aria-label={t('controls.more', 'More')}
                disabled={actions.isDeleting}
              >
                <MoreOutlined />
              </button>
            </Dropdown>
          );
        },
      },
    ],
    [
      t,
      actions.isDeleting,
      actions.downloadingPapFormGuid,
      submitting,
      activatingGuid,
      isLoading,
      handleDownload,
      openEditModal,
    ],
  );

  const renderContent = () => {
    if (!forms.length && !isLoading) {
      return (
        <div className={s['empty-state']}>
          <p>{t('messages.papFormsEmptyState')}</p>
          <BaseButton
            type="primary"
            label={t('labels.uploadPapForm', 'Upload PAP Form')}
            onClick={openCreateModal}
            loading={submitting}
            disabled={!medicationGuid}
          />
        </div>
      );
    }

    return (
      <div className={s['table-wrapper']}>
        <Table
          rowKey="guid"
          columns={columns}
          dataSource={forms}
          pagination={{
            current: currentPage,
            pageSize,
            total: papForms?.total || 0,
            showSizeChanger: true,
            showTotal: (total, range) =>
              t(
                'labels.paginationTotal',
                `${range[0]}-${range[1]} of ${total} items`,
              ),
            onChange: onPageChange,
          }}
          loading={isLoading}
        />
      </div>
    );
  };

  const currentFileName =
    editingPapForm?.fileName || editingPapForm?.formName || undefined;

  return (
    <section className={s.card} aria-label={t('labels.papForms')}>
      <div className={s['pap-header']}>
        <h3 className={s['section-title']}>{t('labels.papForms')}</h3>
        {!!forms.length && (
          <BaseButton
            type="primary"
            label={t('labels.uploadPapForm', 'Upload PAP Form')}
            onClick={openCreateModal}
            loading={submitting}
            disabled={!medicationGuid}
          />
        )}
      </div>

      <BaseLoader loading={isLoading}>{renderContent()}</BaseLoader>

      <PapFormUploadModal
        open={modalOpen}
        submitting={submitting}
        mode={editingPapForm ? 'edit' : 'create'}
        title={
          editingPapForm
            ? t('labels.editPapForm', 'Edit PAP Form')
            : t('labels.uploadPapForm', 'Upload PAP Form')
        }
        initialFormName={editingPapForm?.formName}
        initialNotes={editingPapForm?.notes ?? undefined}
        currentFileName={editingPapForm ? currentFileName : undefined}
        formNameError={formNameError}
        onCancel={closeModal}
        onSubmit={handleSubmit}
      />
    </section>
  );
};

export default MedicationPapForms;
